import React, { useMemo, useState } from 'react';
import { ResponsiveLine } from '@nivo/line';

import SidebarLeft from '../components/SidebarLeft/SidebarLeft';
import Header from '../components/Header';

const assetClasses = [
    'Equity',
    'Bonds',
    'Real Estate',
    'Commodities',
    'Currencies',
    'Crypto',
];

const drivers = [
    {
        id: 'interest',
        label: 'Interest Rates',
        question: 'What is driving the trend of interest rates?',
        prediction: 'Bullish',
        series: [
            { x: '2018', y: 2.4 },
            { x: '2019', y: 1.7 },
            { x: '2020', y: 0.2 },
            { x: '2021', y: 0.1 },
            { x: '2022', y: 2.6 },
            { x: '2023', y: 5.1 },
            { x: '2024', y: 4.8 },
        ],
    },
    {
        id: 'inflation',
        label: 'Inflation',
        question: 'What is driving the trend of inflation?',
        prediction: 'Neutral',
        series: [
            { x: '2018', y: 2.1 },
            { x: '2019', y: 1.8 },
            { x: '2020', y: 1.2 },
            { x: '2021', y: 4.7 },
            { x: '2022', y: 8.0 },
            { x: '2023', y: 4.1 },
            { x: '2024', y: 3.2 },
        ],
    },
    {
        id: 'growth',
        label: 'Growth',
        question: 'What is driving the trend of growth?',
        prediction: 'Bearish',
        series: [
            { x: '2018', y: 3.0 },
            { x: '2019', y: 2.5 },
            { x: '2020', y: -2.8 },
            { x: '2021', y: 5.9 },
            { x: '2022', y: 2.1 },
            { x: '2023', y: 2.5 },
            { x: '2024', y: 2.0 },
        ],
    },
    {
        id: 'unemployment',
        label: 'Unemployment',
        question: 'What is driving the trend of unemployment?',
        prediction: 'Neutral',
        series: [
            { x: '2018', y: 3.9 },
            { x: '2019', y: 3.7 },
            { x: '2020', y: 8.1 },
            { x: '2021', y: 5.4 },
            { x: '2022', y: 3.6 },
            { x: '2023', y: 3.7 },
            { x: '2024', y: 4.0 },
        ],
    },
    {
        id: 'sentiment',
        label: 'Sentiment',
        question: 'What is driving the trend of market sentiment?',
        prediction: 'Bullish',
        series: [
            { x: '2018', y: 68 },
            { x: '2019', y: 74 },
            { x: '2020', y: 52 },
            { x: '2021', y: 78 },
            { x: '2022', y: 49 },
            { x: '2023', y: 63 },
            { x: '2024', y: 71 },
        ],
    },
    {
        id: 'custom',
        label: 'Custom',
        question: 'What is driving the trend of your comparative analysis?',
        prediction: 'Neutral',
        series: [
            { x: '2018', y: 50 },
            { x: '2019', y: 55 },
            { x: '2020', y: 46 },
            { x: '2021', y: 64 },
            { x: '2022', y: 58 },
            { x: '2023', y: 62 },
            { x: '2024', y: 66 },
        ],
    },
];

const correlationRows = [
    ['Equity', 'Positive'],
    ['Bonds', 'Weak Negative'],
    ['Real Estate', 'Strong Positive'],
    ['Commodities', 'Strong Negative'],
    ['Currencies', 'Neutral'],
    ['Crypto', 'Neutral'],
];

const SentimentControl = ({ asset, value, onChange }) => (
    <div className="macro-sentiment-control">
        <strong>{asset}</strong>
        <div>
            {['bearish', 'neutral', 'bullish'].map((option) => (
                <button
                    key={option}
                    type="button"
                    className={value === option ? 'active' : ''}
                    onClick={() => onChange(asset, option)}
                >
                    {option}
                </button>
            ))}
        </div>
    </div>
);

const MacroAnalysis = () => {
    const [country, setCountry] = useState('USA');
    const [activeDriverId, setActiveDriverId] = useState('interest');
    const [sentiment, setSentiment] = useState(() =>
        assetClasses.reduce(
            (accumulator, asset, index) => ({
                ...accumulator,
                [asset]: index % 3 === 0 ? 'bullish' : 'neutral',
            }),
            {}
        )
    );

    const activeDriver = drivers.find((driver) => driver.id === activeDriverId);
    const chartData = useMemo(
        () => [
            {
                id: activeDriver.label,
                data: activeDriver.series,
            },
        ],
        [activeDriver]
    );

    const updateSentiment = (asset, value) => {
        setSentiment((current) => ({ ...current, [asset]: value }));
    };

    return (
        <div className="page-board">
            <div className="subpage-board">
                <div className="board-section-sidebar">
                    <SidebarLeft />
                </div>
                <main className="board-section-center">
                    <div className="board-header-profile">
                        <Header />
                    </div>
                    <div className="board-toolbar">
                        <div>
                            <h3 className="board-title">Macro Analysis</h3>
                            <p className="board-subtitle">{country}</p>
                        </div>
                        <form className="board-search">
                            <input
                                type="text"
                                className="form-control form-control-md"
                                value={country}
                                onChange={(event) =>
                                    setCountry(event.target.value.toUpperCase())
                                }
                                aria-label="Country"
                            />
                            <button
                                className="btn icon-button board-search-button"
                                type="button"
                                aria-label="Search country"
                            >
                                <i className="bi bi-search bi-icon-md" />
                            </button>
                        </form>
                    </div>
                    <div className="macro-shell">
                        <section className="board-card macro-sentiment-card">
                            {assetClasses.map((asset) => (
                                <SentimentControl
                                    key={asset}
                                    asset={asset}
                                    value={sentiment[asset]}
                                    onChange={updateSentiment}
                                />
                            ))}
                        </section>
                        <div className="macro-content-grid">
                            <section className="board-card macro-driver-card">
                                <nav
                                    className="macro-driver-tabs"
                                    aria-label="Macro drivers"
                                >
                                    {drivers.map((driver) => (
                                        <button
                                            key={driver.id}
                                            type="button"
                                            className={
                                                driver.id === activeDriverId
                                                    ? 'active'
                                                    : ''
                                            }
                                            onClick={() =>
                                                setActiveDriverId(driver.id)
                                            }
                                        >
                                            {driver.label}
                                        </button>
                                    ))}
                                </nav>
                            </section>
                            <section className="board-card macro-chart-card">
                                <div className="board-card-header">
                                    <div>
                                        <p className="board-eyebrow">
                                            {country}
                                        </p>
                                        <h4>{activeDriver.label}</h4>
                                    </div>
                                </div>
                                <div className="macro-chart">
                                    <ResponsiveLine
                                        data={chartData}
                                        margin={{
                                            top: 20,
                                            right: 28,
                                            bottom: 48,
                                            left: 56,
                                        }}
                                        xScale={{ type: 'point' }}
                                        yScale={{
                                            type: 'linear',
                                            min: 'auto',
                                            max: 'auto',
                                        }}
                                        axisTop={null}
                                        axisRight={null}
                                        axisBottom={{
                                            tickSize: 0,
                                            tickPadding: 12,
                                        }}
                                        axisLeft={{
                                            tickSize: 0,
                                            tickPadding: 10,
                                        }}
                                        colors={['#3bc6cf']}
                                        pointSize={6}
                                        useMesh={true}
                                        enableGridX={false}
                                        curve="monotoneX"
                                    />
                                </div>
                            </section>
                            <section className="board-card macro-detail-card">
                                <div>
                                    <h4>{activeDriver.label} Driver</h4>
                                    <p>{activeDriver.question}</p>
                                </div>
                                <div>
                                    <h4>Prediction</h4>
                                    <span className="macro-pill">
                                        {activeDriver.prediction}
                                    </span>
                                </div>
                                <div>
                                    <h4>Correlations</h4>
                                    <div className="macro-correlations">
                                        {correlationRows.map(([asset, value]) => (
                                            <React.Fragment key={asset}>
                                                <span>{asset}</span>
                                                <strong>{value}</strong>
                                            </React.Fragment>
                                        ))}
                                    </div>
                                </div>
                            </section>
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default MacroAnalysis;
